import { useState, type FormEvent } from 'react';
import { ArrowLeft, Loader2, MailCheck } from 'lucide-react';
import { forgotPassword } from '@/services/auth';
import { extractErrorMessage } from '@/services/api';

// ForgotPasswordScreen is the "enter your email" step. The server
// response is intentionally identical for known/unknown emails so we
// just show the same "check your inbox" state either way.
export function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not send reset email'));
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    window.location.hash = '';
  };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 p-6">
      <div className="flex flex-col items-center gap-2">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold text-lg">
          U
        </div>
        <h1 className="text-xl font-semibold">Reset your password</h1>
        <p className="text-sm text-text-secondary text-center max-w-xs">
          Enter your email and we'll send a link to choose a new password.
        </p>
      </div>

      {sent ? (
        <div className="card w-full max-w-sm p-5 space-y-4 text-center">
          <div className="flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10 text-green-600">
              <MailCheck className="h-6 w-6" />
            </div>
          </div>
          <h2 className="text-sm font-medium">Check your inbox</h2>
          <p className="text-xs text-text-secondary">
            If an account exists for <span className="font-medium">{email}</span>, a reset
            link is on its way. It expires in 1 hour.
          </p>
          <p className="text-[11px] text-text-secondary">
            Didn't get it? Check spam, or try again.
          </p>
          <button type="button" onClick={goBack} className="btn-ghost text-xs w-full">
            <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="card w-full max-w-sm p-5 space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-text-secondary">Email</label>
            <input
              type="email"
              autoComplete="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoFocus
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
          </button>

          <button type="button" onClick={goBack} className="btn-ghost text-xs w-full">
            <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back to sign in
          </button>
        </form>
      )}
    </div>
  );
}
